import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useRouterState,
} from '@tanstack/react-router'
import {
  AppShell,
  Burger,
  Group,
  NavLink as MantineNavLink,
  ScrollArea,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconCpu,
  IconBuildingStore,
  IconCreditCard,
  IconDashboard,
  IconDevices,
  IconFileText,
  IconLogout,
  IconSettings,
  IconUsers,
} from '@tabler/icons-react'
import { getSession } from '../lib/auth-functions'
import { authClient } from '../lib/auth-client'
import { checkAdminAccess } from '../admin/server-fns'
import { AdminRouteError, kickerCls } from '../admin/ui'
import ThemeToggle from '../components/ThemeToggle'

const navSections = [
  {
    label: 'Fleet',
    items: [
      { label: 'Dashboard', to: '/admin', icon: IconDashboard },
      { label: 'Devices', to: '/admin/devices', icon: IconDevices },
      { label: 'Sites', to: '/admin/sites', icon: IconBuildingStore },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Payments', to: '/admin/payments', icon: IconCreditCard },
      { label: 'Firmware', to: '/admin/firmware', icon: IconCpu },
      { label: 'Configs', to: '/admin/configs', icon: IconSettings },
    ],
  },
  {
    label: 'Access',
    items: [
      { label: 'Users', to: '/admin/users', icon: IconUsers },
      { label: 'Audit logs', to: '/admin/audit-logs', icon: IconFileText },
    ],
  },
] as const

export const Route = createFileRoute('/admin')({
  beforeLoad: async () => {
    /* A session cookie is not enough — it survives a revoked Zitadel grant.
       checkAdminAccess re-checks the access token with Zitadel, so anything
       short of a live token bounces to /login rather than /no-access, which
       is for the one case re-authenticating cannot fix. */
    const access = await checkAdminAccess()
    if (!access.allowed) {
      throw redirect({ to: access.denial === 'missing-role' ? '/no-access' : '/login' })
    }
    const session = await getSession()
    if (!session) {
      throw redirect({ to: '/login' })
    }
    return { session }
  },
  component: AdminShell,
  errorComponent: AdminRouteError,
})

const navLinkClasses = {
  root: `relative mb-0.5 rounded-md px-2.5 py-2 font-medium text-slate-600 transition-colors before:content-[''] hover:bg-slate-100 hover:text-slate-900 data-active:bg-blue-700/10 data-active:font-semibold data-active:text-blue-700 data-active:before:absolute data-active:before:left-0 data-active:before:top-1/2 data-active:before:h-4 data-active:before:w-0.5 data-active:before:-translate-y-1/2 data-active:before:rounded-full data-active:before:bg-blue-700 data-active:hover:bg-blue-700/15 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100 dark:data-active:bg-blue-400/10 dark:data-active:text-blue-400 dark:data-active:before:bg-blue-400 dark:data-active:hover:bg-blue-400/15`,
  label: 'text-sm',
}

function AdminShell() {
  const [opened, { toggle, close }] = useDisclosure()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  /* Single source of truth for the active item — Link's own fuzzy matching
     would light up /admin on every subpage. */
  const currentPath = pathname.replace(/\/+$/, '') || '/'
  const { session } = Route.useRouteContext()

  const handleLogout = () => {
    authClient.signOut().then(() => {
      window.location.href = '/login'
    })
  }

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="lg"
    >
      <AppShell.Header className="border-slate-200 bg-white/85 backdrop-blur-lg dark:border-slate-800 dark:bg-slate-900/85">
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Link
              to="/admin"
              className="inline-flex items-center gap-2.5 text-slate-900 no-underline dark:text-white"
            >
              <svg
                viewBox="0 0 16 16"
                width="18"
                height="18"
                aria-hidden="true"
                className="flex-shrink-0"
              >
                <rect x="0" y="0" width="7" height="7" rx="1.5" fill="currentColor" />
                <rect x="9" y="0" width="7" height="7" rx="1.5" fill="currentColor" />
                <rect x="0" y="9" width="7" height="7" rx="1.5" fill="currentColor" />
                <rect
                  x="9"
                  y="9"
                  width="7"
                  height="7"
                  rx="1.5"
                  className="fill-blue-700 dark:fill-blue-400"
                />
              </svg>
              <span className="text-base font-bold tracking-tight font-stretch-[112%]">
                Lugstash
              </span>
              <span className={`${kickerCls} mt-0.5`}>Console</span>
            </Link>
          </Group>
          <Group gap="sm" wrap="nowrap">
            <Link
              to="/"
              className="hidden text-xs font-semibold text-slate-500 no-underline transition hover:text-slate-900 sm:inline dark:text-slate-400 dark:hover:text-white"
            >
              ← Website
            </Link>
            <ThemeToggle />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar
        p="md"
        className="border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
      >
        <AppShell.Section grow component={ScrollArea}>
          {navSections.map((section) => (
            <div key={section.label} className="mb-6">
              <p className={`${kickerCls} mb-2 px-2.5`}>{section.label}</p>
              {section.items.map((item) => (
                <MantineNavLink
                  key={item.to}
                  component={Link}
                  to={item.to}
                  activeOptions={{ exact: true }}
                  activeProps={{}}
                  label={item.label}
                  leftSection={<item.icon size={16} stroke={1.75} />}
                  active={currentPath === item.to}
                  onClick={close}
                  classNames={navLinkClasses}
                />
              ))}
            </div>
          ))}
        </AppShell.Section>

        <AppShell.Section className="-mx-2.5 border-t border-slate-200 px-2.5 pt-3 dark:border-slate-800">
          <div className="mb-2 flex items-center gap-2.5 px-2.5">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-blue-700/10 font-mono text-[0.65rem] font-semibold uppercase text-blue-700 dark:bg-blue-400/10 dark:text-blue-400">
              {(session.user?.name ?? session.user?.email ?? 'O').slice(0, 1)}
            </span>
            <span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-500 dark:text-slate-400">
              {session.user?.name ?? session.user?.email ?? 'Operator'}
            </span>
          </div>
          <MantineNavLink
            component="button"
            label="Sign out"
            leftSection={<IconLogout size={16} stroke={1.75} />}
            onClick={handleLogout}
            classNames={{
              root: 'rounded-md px-2.5 py-2 font-medium text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400',
              label: 'text-sm',
            }}
          />
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main className="bg-slate-100 dark:bg-slate-950">
        <div className="mx-auto w-full max-w-5xl">
          <Outlet />
        </div>
      </AppShell.Main>
    </AppShell>
  )
}
